import React, { useState, useEffect, useCallback } from "react";
import { Form, Image, Button, Dropdown, Checkbox } from "semantic-ui-react";
import { map } from "lodash";
import { useDropzone } from "react-dropzone";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useCategory, useProduct, useExtraGroup } from "../../../../hooks";
import "./AddEditProductForm.scss";

export function AddEditProductForm(props) {
  const { onClose, onRefetch, product } = props;
  const [categoriesFormat, setCategoriesFormat] = useState([]);
  const [extraGroupsFormat, setExtraGroupsFormat] = useState([]);
  const [previewImage, setPreviewImage] = useState(
    product ? product?.image : null
  );
  const { categories, getCategories } = useCategory();
  const { addProduct, updateProduct } = useProduct();
  const { extraGroups, getExtraGroups } = useExtraGroup();

  useEffect(() => {
    getCategories();
    getExtraGroups();
  }, []);
  useEffect(() => {
    setCategoriesFormat(formatDropdownData(categories));
  }, [categories]);
  useEffect(() => {
    setExtraGroupsFormat(
      map(extraGroups, (item) => ({
        key: item.id,
        text: item.label,
        value: item.id,
      }))
    );
  }, [extraGroups]);

  const formik = useFormik({
    initialValues: initialValues(product),
    validationSchema: Yup.object(product ? updateSchema() : newSchema()),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (product) await updateProduct(product.id, formValue);
      else await addProduct(formValue);

      onRefetch();
      onClose();
    },
  });

  const onDrop = useCallback(async (acceptedFile) => {
    const file = acceptedFile[0];
    await formik.setFieldValue("image", file);
    await formik.setFieldValue("removeImage", false);
    setPreviewImage(URL.createObjectURL(file));
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/jpeg, image/png",
    noKeyboard: true,
    multiple: false,
    onDrop,
  });

  const removeImage = () => {
    setPreviewImage(null);
    formik.setFieldValue("image", "");
    formik.setFieldValue("removeImage", true);
  };

  return (
    <Form className="add-edit-product-form" onSubmit={formik.handleSubmit}>
      <Form.Input
        name="title"
        placeholder="Nombre del producto"
        value={formik.values.title}
        onChange={formik.handleChange}
        error={formik.errors.title}
      />
      <Form.TextArea
        name="description"
        placeholder="Descripción del producto"
        value={formik.values.description}
        onChange={formik.handleChange}
        error={formik.errors.description}
      />
      <Form.Input
        type="number"
        name="price"
        label="Precio"
        placeholder="Precio"
        value={formik.values.price}
        onChange={formik.handleChange}
        error={formik.errors.price}
      />
      <Form.Input
        type="number"
        name="estimatedCost"
        label="Costo estimado (opcional)"
        placeholder="Costo estimado"
        value={formik.values.estimatedCost}
        onChange={formik.handleChange}
        error={formik.errors.estimatedCost}
      />

      <Dropdown
        placeholder="Categoria"
        fluid
        selection
        search
        options={categoriesFormat}
        value={formik.values.category}
        error={formik.errors.category}
        onChange={(_, data) => formik.setFieldValue("category", data.value)}
      />

      <Form.Input
        type="number"
        name="order"
        min="0"
        label="Número de aparición (dentro de la categoría)"
        placeholder="0"
        value={formik.values.order}
        onChange={formik.handleChange}
        error={formik.errors.order}
      />

      <Form.Input
        name="optionLabel"
        placeholder="Nombre de la opción (ej. Elige el tipo de proteína)"
        value={formik.values.optionLabel}
        onChange={formik.handleChange}
        error={formik.errors.optionLabel}
      />
      <Form.Input
        name="optionChoices"
        placeholder="Opciones separadas por coma. Con precio: Chica:2, Grande:4. Sin precio: Pollo, Carne"
        value={formik.values.optionChoices}
        onChange={formik.handleChange}
        error={formik.errors.optionChoices}
      />
      {formik.values.optionLabel && formik.values.optionChoices && (
        <>
          <Form.Input
            type="number"
            name="optionChoiceCount"
            min="1"
            label="Cantidad a elegir"
            placeholder="1"
            value={formik.values.optionChoiceCount}
            onChange={formik.handleChange}
            error={formik.errors.optionChoiceCount}
          />
          <div className="add-edit-product-form__active">
            <Checkbox
              toggle
              checked={formik.values.optionRequired}
              onChange={(_, data) =>
                formik.setFieldValue("optionRequired", data.checked)
              }
            />
            Selección obligatoria
          </div>
        </>
      )}

      <Dropdown
        placeholder="Grupos de adicionales (opcional)"
        fluid
        multiple
        selection
        options={extraGroupsFormat}
        value={formik.values.extraGroups}
        onChange={(_, data) => formik.setFieldValue("extraGroups", data.value)}
      />

      <div className="add-edit-product-form__active">
        <Checkbox
          toggle
          checked={formik.values.active}
          onChange={(_, data) => formik.setFieldValue("active", data.checked)}
        />
        Producto activo
      </div>

      <Button
        type="button"
        fluid
        {...getRootProps()}
        color={formik.errors.image && "red"}
      >
        {previewImage ? "Cambiar imagen" : "Subir imagen (opcional)"}
      </Button>
      <input {...getInputProps()} />
      {previewImage && (
        <div className="add-edit-product-form__image-preview">
          <Image src={previewImage} />
          <Button type="button" basic negative fluid onClick={removeImage}>
            Eliminar imagen
          </Button>
        </div>
      )}

      <Button
        type="submit"
        primary
        fluid
        content={product ? "Actualizar" : "Crear"}
      />
    </Form>
  );
}

function formatOptionChoices(choices) {
  if (!choices?.length) return "";

  return choices
    .map((choice) => {
      if (typeof choice === "string") return choice;
      return choice.price ? `${choice.label}:${choice.price}` : choice.label;
    })
    .join(", ");
}

function formatDropdownData(data) {
  return map(data, (item) => ({
    key: item.id,
    text: item.title,
    value: item.id,
  }));
}

function initialValues(data) {
  return {
    title: data?.title || "",
    description: data?.description || "",
    price: data?.price || "",
    estimatedCost: data?.estimated_cost ?? "",
    category: data?.category || "",
    order: data?.order ?? 0,
    active: data?.active ? true : false,
    optionLabel: data?.option?.label || "",
    optionChoices: formatOptionChoices(data?.option?.choices),
    optionRequired: data?.option ? data.option.required : true,
    optionChoiceCount: data?.option?.choice_count ?? 1,
    extraGroups: map(data?.extra_groups_data, "id") || [],
    image: "",
    removeImage: false,
  };
}

function newSchema() {
  return {
    title: Yup.string().required(true),
    description: Yup.string(),
    price: Yup.number().required(true),
    estimatedCost: Yup.number(),
    category: Yup.number().required(true),
    order: Yup.number().min(0),
    active: Yup.boolean().required(true),
    optionLabel: Yup.string(),
    optionChoices: Yup.string(),
    optionRequired: Yup.boolean(),
    optionChoiceCount: Yup.number().min(1),
    extraGroups: Yup.array(),
    image: Yup.string(),
  };
}

function updateSchema() {
  return {
    title: Yup.string().required(true),
    description: Yup.string(),
    price: Yup.number().required(true),
    estimatedCost: Yup.number(),
    category: Yup.number().required(true),
    order: Yup.number().min(0),
    active: Yup.boolean().required(true),
    optionLabel: Yup.string(),
    optionChoices: Yup.string(),
    optionRequired: Yup.boolean(),
    optionChoiceCount: Yup.number().min(1),
    extraGroups: Yup.array(),
    image: Yup.string(),
  };
}
